import React, { useState } from 'react';
import { Activity, MessageSquare, AlertTriangle } from 'lucide-react';
import { useBPData } from '../hooks/useBPData';
import RecentReadings from '../components/RecentReadings';
import BPChart from '../components/BPChart';
import BPEntryForm from '../components/BPEntryForm';
import ChatInterface from '../components/ChatInterface';

interface DashboardProps {
  userId?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ userId = 'default' }) => {
  const [showBPForm, setShowBPForm] = useState(false);
  const [showSymptoms, setShowSymptoms] = useState(false);
  const { readings, trend, loading, error, addReading, deleteReading } = useBPData(userId);

  const handleAddReading = async (reading: any) => {
    await addReading(reading);
  };

  const handleDeleteReading = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this reading?')) {
      await deleteReading(id);
    }
  };

  const getTrendMessage = () => {
    if (!trend) return null;
    
    switch (trend.trend_direction) {
      case 'improving':
        return {
          message: 'Your blood pressure trend is improving! Keep up the good work.',
          color: 'text-green-600 bg-green-50 border-green-200'
        };
      case 'concerning':
        return {
          message: 'Your recent readings show an upward trend. Consider discussing with your healthcare provider.',
          color: 'text-red-600 bg-red-50 border-red-200'
        };
      case 'stable':
        return {
          message: 'Your blood pressure readings are stable.',
          color: 'text-blue-600 bg-blue-50 border-blue-200'
        };
      default:
        return null;
    }
  };

  const trendMessage = getTrendMessage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center">
            <AlertTriangle className="w-5 h-5 Mr-3 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Trend Alert */}
        {trendMessage && (
          <div className={`border p-4 rounded-lg flex items-center ${trendMessage.color}`}>
            <Activity className="w-5 h-5 mr-3 flex-shrink-0" />
            <p className="font-medium">{trendMessage.message}</p>
          </div>
        )}

        {/* Recent Readings */}
        <RecentReadings 
          readings={readings}
          onDeleteReading={handleDeleteReading}
          loading={loading}
        />

        {/* BP Chart */}
        <BPChart readings={readings} />

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setShowBPForm(true)}
            className="btn-primary flex items-center justify-center"
          >
            <Activity className="w-5 h-5 mr-2" />
            Enter BP
          </button>
          
          <button
            onClick={() => setShowSymptoms(true)}
            className="btn-primary flex items-center justify-center"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Symptoms
          </button>
        </div>

        {/* Chat Interface */}
        <ChatInterface userId={userId} />

        {/* BP Entry Modal */}
        <BPEntryForm
          isOpen={showBPForm}
          onClose={() => setShowBPForm(false)}
          onSubmit={handleAddReading}
          loading={loading}
        />

        {/* Symptoms Modal (placeholder) */}
        {showSymptoms && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Symptom Tracker</h2>
              <p className="text-gray-600 mb-6">
                This feature is coming soon! For now, you can discuss any symptoms with MEGI in the chat below.
              </p>
              <button
                onClick={() => setShowSymptoms(false)}
                className="btn-primary w-full"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;